import { ampli } from '../../../../ampli';
import { EvercycleOption, EvercycleProgram, EvercycleValuation } from '../../../../data/models/recommendations.models';

/**
 * Amplitude event names emitted by the EverCycle Buyback surfaces.
 *
 * Payload rules (CUJ 002 / 003 spec):
 * - Never include customer PII beyond Amplitude's own user id.
 * - `recommendation_id`, `program`, `org_id` identify the recommendation.
 * - `option` is included for `evercycle_option_selected` only.
 * - `offer_id` and `total_amount_bucket` (bucketed to keep cardinality low)
 *   are included for `evercycle_offer_accepted` only.
 *
 * Dispatched through the generic `ampli.track()` with an explicit
 * `event_type` + `event_properties` envelope — the generated `ampli` type is
 * produced by `ampli pull spog-web` in another repo, so new event types
 * cannot originate here. Follows the pattern in
 * `service-catalog/edi-analytics.ts:43`.
 */
export const EVERCYCLE_EVENTS = {
    BADGE_IMPRESSION: 'evercycle_badge_impression',
    CARD_IMPRESSION: 'evercycle_card_impression',
    DIALOG_OPENED: 'evercycle_dialog_opened',
    OPTION_SELECTED: 'evercycle_option_selected',
    OFFER_ACCEPTED: 'evercycle_offer_accepted',
} as const;

export interface EvercycleBasePayload {
    readonly recommendation_id: string;
    readonly program: EvercycleProgram;
    readonly org_id: string;
}

export interface EvercycleOptionSelectedPayload extends EvercycleBasePayload {
    readonly option: EvercycleOption;
}

export interface EvercycleOfferAcceptedPayload extends EvercycleBasePayload {
    readonly offer_id: string;
    readonly total_amount_bucket: EvercycleValuation['totalAmountBucket'];
}

/**
 * Emit an EverCycle Amplitude event. The payload travels in `event_properties`
 * — the shape the Amplitude tracking plan expects — not in `EventOptions`,
 * which `ampli.track(name, properties)` would misroute.
 *
 * The call is swallowed when `ampli` is not initialized (dev / specs), so a
 * telemetry outage never prevents the surface from rendering.
 */
export function emitEvercycleEvent(
    event: (typeof EVERCYCLE_EVENTS)[keyof typeof EVERCYCLE_EVENTS],
    payload: EvercycleBasePayload | EvercycleOptionSelectedPayload | EvercycleOfferAcceptedPayload,
): void {
    try {
        ampli.track({ event_type: event, event_properties: payload });
    } catch {
        // Analytics failures are not surfaced to the user.
    }
}
